import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useVisibilityAwareInterval } from '@/hooks/useVisibilityAwareInterval';
import {
  getDashboardMetrics,
  getExecutionTrends,
  getPerformanceBreakdown,
  getModemActivity,
  getRecentActivity,
  DashboardMetrics,
  ExecutionTrendData,
  PerformanceBreakdown,
  ModemActivityData,
  RecentActivityItem,
} from '@/services/analyticsService';
import { handleError } from '@/utils/error-handler';

export interface DashboardData {
  metrics: DashboardMetrics | null;
  executionTrends: ExecutionTrendData[];
  performanceBreakdown: PerformanceBreakdown | null;
  modemActivity: ModemActivityData[];
  recentActivity: RecentActivityItem[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for fetching and managing dashboard data
 */
export function useDashboardMetrics(hoursRange: number = 24, refreshInterval?: number) {
  const { activeOrganization } = useAuth();
  const [data, setData] = useState<DashboardData>({
    metrics: null,
    executionTrends: [],
    performanceBreakdown: null,
    modemActivity: [],
    recentActivity: [],
    loading: true,
    error: null,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadDashboardData = async () => {
    if (!activeOrganization?.id) {
      setData((prev) => ({
        ...prev,
        loading: false,
        error: 'No organization selected for viewing',
      }));
      return;
    }

    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      // Fetch all data in parallel, each independently. allSettled — NOT all —
      // so one ancillary feed failing can't blank the whole dashboard: a single
      // unreadable execution→flow reference used to reject Promise.all, wiping
      // every metric and surfacing a "permission denied" banner even though the
      // core reads succeeded. Each section now falls back to its empty value.
      const orgId = activeOrganization.id;
      const [metrics, executionTrends, performanceBreakdown, modemActivity, recentActivity] =
        await Promise.allSettled([
          getDashboardMetrics(orgId),
          getExecutionTrends(orgId, hoursRange),
          getPerformanceBreakdown(orgId),
          getModemActivity(orgId, hoursRange),
          getRecentActivity(orgId, 5),
        ]);

      const settled = <T,>(r: PromiseSettledResult<T>, label: string, fallback: T): T => {
        if (r.status === 'fulfilled') return r.value;
        console.error(`Dashboard: ${label} failed to load`, r.reason);
        return fallback;
      };

      setData({
        metrics: settled(metrics, 'metrics', null),
        executionTrends: settled(executionTrends, 'execution trends', []),
        performanceBreakdown: settled(performanceBreakdown, 'performance breakdown', null),
        modemActivity: settled(modemActivity, 'modem activity', []),
        recentActivity: settled(recentActivity, 'recent activity', []),
        loading: false,
        // Only surface a page-level error when the core metrics themselves fail;
        // a failed side-panel feed degrades silently to its empty state.
        error:
          metrics.status === 'rejected'
            ? handleError(metrics.reason).userMessage || 'Failed to load dashboard data'
            : null,
      });
    } catch (error) {
      const appError = handleError(error);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: appError.userMessage || 'Failed to load dashboard data',
      }));
    }
  };

  const refresh = async () => {
    setIsRefreshing(true);
    await loadDashboardData();
    setIsRefreshing(false);
  };

  useEffect(() => {
    loadDashboardData();
  }, [activeOrganization?.id, hoursRange]);

  // Visibility-aware auto-refresh: pauses when tab is hidden
  useVisibilityAwareInterval(loadDashboardData, refreshInterval || 60000, !!refreshInterval);

  return {
    ...data,
    refresh,
    isRefreshing,
  };
}

